import React, { useEffect, useMemo, useState } from 'react';
import { Select, Button, Modal, Tag, Space, Tooltip, notification } from 'antd';
import { RocketOutlined } from '@ant-design/icons';
import pmApi from './pmApi';

/**
 * Per-plan setup — what used to be the standalone Cutover tab. Cutover is not
 * a global setting: every plan has its own, so it lives beside the design it
 * applies to. Values are chosen first and committed together with Save all;
 * the consequence of each choice is stated inline instead of in a popup.
 */

const STATUS_FIELD = {
  key: 'lifecycle',
  label: 'Status',
  options: ['draft', 'published', 'retired'],
  optionLabels: { draft: 'Draft', published: 'Published', retired: 'Retired' },
  consequence: {
    draft: 'The design is still being worked on. Launch stays locked.',
    published: 'The design is complete and approved — this unlocks Launch.',
    retired: 'The design is no longer current. Existing policies keep working.',
  },
};

const STATE_FIELDS = [
  {
    key: 'mode',
    label: 'Enforcement',
    options: ['ignore', 'advisory', 'enforce_strict'],
    optionLabels: { ignore: 'Ignore', advisory: 'Advisory', enforce_strict: 'Enforce Strictly' },
    consequence: {
      ignore: 'No enforcement. PA screens are identical to today, no telemetry.',
      advisory: 'The plan-model panel appears on PA review and every would-block verdict is recorded. Nothing blocks.',
      enforce_strict: 'Blocker: PA approvals are refused on not-covered benefits and pool breaches. Flip back to Advisory to stop blocking instantly.',
    },
    danger: 'enforce_strict',
  },
  {
    key: 'pricing_source',
    label: 'Pricing master',
    options: ['legacy', 'pm'],
    optionLabels: { legacy: 'Legacy', pm: 'Plan Management' },
    consequence: {
      legacy: 'Legacy price columns are the source; plan management mirrors them.',
      pm: 'Plan + Band prices become the list layer (negotiated org values still win); legacy columns become the mirror.',
    },
    danger: 'pm',
  },
  {
    key: 'brochure_source',
    label: 'Brochure source',
    options: ['legacy', 'pm'],
    optionLabels: { legacy: 'Legacy', pm: 'Plan Management' },
    consequence: {
      legacy: 'The enrollee-facing brochure reads the legacy tables.',
      pm: 'The brochure is built from this plan’s coverage rows.',
    },
    danger: 'pm',
  },
  {
    key: 'org_filter',
    label: 'Org plan filter',
    options: ['legacy', 'plan_type'],
    optionLabels: { legacy: 'Legacy rules', plan_type: 'By plan type' },
    consequence: {
      legacy: 'Organization plan attach uses the legacy audience/segment rules.',
      plan_type: 'Retail plans are rejected from organization attach, by declared plan type.',
    },
    danger: null,
  },
];

const ALL_FIELDS = [STATUS_FIELD, ...STATE_FIELDS];

const valueTag = (value) => {
  if (!value || value === 'ignore' || value === 'off' || value === 'legacy' || value === 'draft') return null;
  if (value === 'advisory' || value === 'log') return <Tag color="gold" style={{ marginInlineEnd: 0 }}>Advisory</Tag>;
  if (value === 'enforce_strict' || value === 'enforce') return <Tag color="red" style={{ marginInlineEnd: 0 }}>Enforcing</Tag>;
  return <Tag color="green" style={{ marginInlineEnd: 0 }}>{value}</Tag>;
};

const currentOf = (plan, field) => plan?.[field.key] || field.options[0];

export default function SetupSection({ planId, plan, onChanged }) {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  // Re-seed whenever the plan or any committed value changes, so the pickers
  // show what is live rather than a stale edit.
  const committed = useMemo(
    () => ALL_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: currentOf(plan, f) }), {}),
    [plan],
  );

  useEffect(() => { setDraft(committed); }, [committed, planId]);

  const dirtyKeys = ALL_FIELDS
    .map((f) => f.key)
    .filter((key) => draft[key] !== undefined && draft[key] !== committed[key]);

  const saveAll = async () => {
    if (!dirtyKeys.length) return;
    setSaving(true);
    try {
      let ok = true;
      if (dirtyKeys.includes('lifecycle')) {
        const res = await pmApi.setProfile(planId, { lifecycle: draft.lifecycle });
        ok = ok && res.ok;
      }
      const statePatch = {};
      STATE_FIELDS.forEach((f) => {
        if (dirtyKeys.includes(f.key)) statePatch[f.key] = draft[f.key];
      });
      if (Object.keys(statePatch).length) {
        const res = await pmApi.setState(planId, statePatch);
        ok = ok && res.ok;
      }
      if (ok) {
        notification.success({
          message: 'Setup saved',
          description: `${dirtyKeys.length} change(s) applied to ${plan?.plan_name || 'this plan'}. Audited and reversible.`,
        });
        onChanged?.();
      }
    } finally {
      setSaving(false);
    }
  };

  const readiness = [
    { label: 'Design (coverage rows)', ok: Number(plan?.coverage_rows) > 0, detail: `${plan?.coverage_rows || 0} rows` },
    { label: 'Bands priced', ok: Number(plan?.priced_bands) > 0, detail: `${plan?.priced_bands || 0} bands` },
    { label: 'Design published', ok: committed.lifecycle === 'published', detail: committed.lifecycle || 'no profile' },
  ];
  const launchReady = readiness.every((r) => r.ok);

  // Grey-after-launch: once launched, the button stays disabled until the API
  // reports a change (design, pools, prices, org overrides, setup) made after
  // the last launch. launched_at is null for plans never launched through the
  // workspace, which keeps them launchable as before.
  const launchedAt = plan?.launched_at || null;
  const changedSinceLaunch = plan?.changed_since_launch !== false;
  const launchedUnchanged = Boolean(launchedAt) && !changedSinceLaunch;
  const launchedLabel = launchedAt ? String(launchedAt).slice(0, 16).replace('T', ' ') : null;

  const launch = () => {
    Modal.confirm({
      title: `Launch ${plan?.plan_name || 'plan'}?`,
      content: 'Makes the plan sellable in its declared segment. This is the deliberate commercial act plan creation defers. Audited.',
      okText: 'Launch plan',
      onOk: async () => {
        const res = await pmApi.launchPlan(planId);
        if (res.ok) {
          notification.success({ message: 'Launched', description: res.message });
          onChanged?.();
        }
      },
    });
  };

  return (
    <>
      <h5>Setup</h5>
      {ALL_FIELDS.map((field) => (
        <div key={field.key} style={{ marginBottom: 10 }}>
          <div className="pm-rail-row" style={{ marginBottom: 2 }}>
            <span>{field.label}</span>
            {valueTag(committed[field.key])}
          </div>
          <Select
            size="small"
            style={{ width: '100%' }}
            value={draft[field.key] ?? committed[field.key]}
            onChange={(v) => setDraft((d) => ({ ...d, [field.key]: v }))}
            options={field.options.map((o) => ({ value: o, label: field.optionLabels?.[o] || o }))}
          />
          <div className="pm-benefit-sub" style={{ marginTop: 3 }}>
            {field.consequence[draft[field.key] ?? committed[field.key]]}
          </div>
        </div>
      ))}

      <Button
        type="primary"
        size="small"
        block
        loading={saving}
        disabled={!dirtyKeys.length}
        onClick={saveAll}
      >
        {dirtyKeys.length ? `Save all (${dirtyKeys.length})` : 'Save all'}
      </Button>

      <hr />
      <h5>Readiness</h5>
      {readiness.map((r) => (
        <div key={r.label} className="pm-rail-row">
          <span>{r.ok ? '✓' : '·'} {r.label}</span>
          <b style={{ color: r.ok ? 'var(--pm-accent-ink)' : 'var(--pm-ink-faint)' }}>{r.detail}</b>
        </div>
      ))}
      <Space direction="vertical" size={4} style={{ width: '100%', marginTop: 8 }}>
        <Tooltip
          title={launchedUnchanged
            ? `Launched ${launchedLabel}. Nothing has changed since — change the design, prices or setup to launch again.`
            : null}
        >
          <Button
            icon={<RocketOutlined />}
            size="small"
            block
            type={launchedAt && changedSinceLaunch ? 'primary' : 'default'}
            disabled={!launchReady || launchedUnchanged}
            onClick={launch}
          >
            {launchedUnchanged ? 'Launched' : launchedAt ? 'Launch again' : 'Launch plan'}
          </Button>
        </Tooltip>
        {!launchReady ? (
          <span className="pm-benefit-sub">
            Launch unlocks when the status is Published and at least one band is priced.
          </span>
        ) : launchedUnchanged ? (
          <span className="pm-benefit-sub">
            Launched {launchedLabel}. Unlocks again after the next change to this plan.
          </span>
        ) : launchedAt ? (
          <span className="pm-benefit-sub">
            Changed since the launch on {launchedLabel} — launch again to put the changes on sale.
          </span>
        ) : null}
      </Space>
    </>
  );
}
